import { forwardRef, HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { useTheme } from '../theme';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  theme?: 'light' | 'dark'; // Allow explicit theme override
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'info', title, children, onDismiss, theme, ...props }, ref) => {
    const { isLightTheme: contextIsLightTheme } = useTheme();

    // Use explicit theme prop if provided, otherwise fall back to context
    const isLightTheme = theme ? theme === 'light' : contextIsLightTheme;

    const baseClasses = 'relative rounded-lg border p-4 mb-4';

    // Theme-aware variants
    const lightVariants = {
      error: 'border-red-300 bg-red-50 text-red-800',
      warning: 'border-yellow-300 bg-yellow-50 text-yellow-800',
      info: 'border-blue-300 bg-blue-50 text-blue-800',
      success: 'border-green-300 bg-green-50 text-green-800',
    };

    const darkVariants = {
      error: 'border-red-500/50 bg-red-900/20 text-red-200',
      warning: 'border-yellow-500/50 bg-yellow-900/20 text-yellow-200',
      info: 'border-blue-500/50 bg-blue-900/20 text-blue-200',
      success: 'border-green-500/50 bg-green-900/20 text-green-200',
    };

    const variants = isLightTheme ? lightVariants : darkVariants;

    const iconVariants = {
      error: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      warning: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      info: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      success: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    };

    return (
      <div
        className={clsx(
          baseClasses,
          variants[variant],
          className
        )}
        ref={ref}
        {...props}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-3 mt-0.5">
            {iconVariants[variant]}
          </div>
          <div className="flex-1">
            {title && (
              <h4 className="font-medium mb-1">
                {title}
              </h4>
            )}
            <div>
              {children}
            </div>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className={clsx(
                "flex-shrink-0 ml-3 p-1 rounded transition-colors",
                isLightTheme ? "hover:bg-black/10" : "hover:bg-white/10"
              )}
              aria-label="Dismiss alert"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';

export { Alert };
